import { useRef } from "react";

import { usePolarityDomain } from "@/shared/types/polarity";
import { formatInteger } from "@/shared/utils/format";

import { ChartLegend, polarityLegend, polaritySegments, useChartTip, useChartWidth } from "./chart-kit";
import { EmptyState } from "./EmptyState";

/** Uma categoria: o volume quebrado por polaridade e, opcional, o
 *  indicador escolhido já escrito (`metric`). */
export type PolarityBarItem = {
  key: string;
  label: string;
  count: number;
  byPolarity: Record<string, number>;
  metric?: string;
};

const ROW = 30;
const BAR = 14;
const GAP = 2;

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, Math.max(1, max - 1))}…` : text;
}

/**
 * Volume por categoria, empilhado por sentimento — o "Sentimento &
 * Indicadores por …" do dashboard do v1, deitado: o nome de veículo,
 * tema e porta-voz é longo, e na horizontal ele cabe inteiro.
 *
 * **O indicador NÃO ganha um segundo eixo** (o v1 desenhava uma linha de
 * CENA sobre as barras de volume, numa segunda escala): vai numa coluna
 * de valor à direita, alinhada à barra. Dois eixos no mesmo gráfico
 * fazem quem lê comparar grandezas que não se comparam.
 */
export function PolarityBars({ items, percent = false, metricLabel, noun = "menções" }: {
  items: PolarityBarItem[];
  percent?: boolean;
  metricLabel?: string;
  noun?: string;
}) {
  // Redesenha quando o domínio chega: sem ele, toda fatia é "sem classificação".
  usePolarityDomain();
  const [frame, width] = useChartWidth<HTMLDivElement>();
  const svgRef = useRef<SVGSVGElement>(null);
  const { bind, tip } = useChartTip(frame);
  if (!items.length) return <EmptyState/>;

  const hasMetric = items.some(item => item.metric !== undefined);
  const labelWidth = Math.min(220, Math.max(110, width * 0.3));
  const totalWidth = 58;
  const metricWidth = hasMetric ? 76 : 0;
  const barX = labelWidth + 8;
  const barWidth = Math.max(60, width - barX - totalWidth - metricWidth - 8);
  const max = Math.max(...items.map(item => item.count), 1);
  const headerHeight = hasMetric ? 18 : 0;
  const height = headerHeight + items.length * ROW + 4;
  const legend = polarityLegend(items.map(item => item.byPolarity), percent);

  return (
    <div className="chart-frame" ref={frame}>
      <svg ref={svgRef} data-export width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`Volume por sentimento em ${items.length} categorias`}>
        {hasMetric && (
          <text x={width - 4} y={12} className="chart-axis-text" textAnchor="end">{metricLabel}</text>
        )}
        {items.map((item, index) => {
          const y = headerHeight + index * ROW;
          const segments = polaritySegments(item.byPolarity, { percent });
          const scale = percent ? barWidth / 100 : (barWidth * (item.count / max)) / Math.max(item.count, 1);
          let cursor = barX;
          const detail = polaritySegments(item.byPolarity).filter(segment => segment.value > 0);
          const tipContent = {
            title: item.label,
            lines: [
              { label: "Total", value: formatInteger(item.count) },
              ...detail.map(segment => ({
                label: segment.label,
                tone: segment.tone,
                value: `${formatInteger(segment.value)}${item.count ? ` · ${Math.round((segment.value / item.count) * 100)}%` : ""}`
              })),
              ...(item.metric !== undefined && metricLabel ? [{ label: metricLabel, value: item.metric }] : [])
            ]
          };
          return (
            <g key={item.key} className="chart-row" tabIndex={0} aria-label={`${item.label}: ${formatInteger(item.count)} ${noun}`} {...bind(tipContent)}>
              <rect x={0} y={y} width={width} height={ROW} className="chart-row-hit"/>
              <text x={labelWidth} y={y + ROW / 2} className="chart-label-text" textAnchor="end" dominantBaseline="middle">
                {truncate(item.label, Math.floor(labelWidth / 7))}
              </text>
              {segments.map(segment => {
                const length = segment.value * scale;
                if (length <= 0) return null;
                const x = cursor;
                cursor += length;
                return (
                  <rect
                    key={segment.key}
                    x={x}
                    y={y + (ROW - BAR) / 2}
                    width={Math.max(length - GAP, 1)}
                    height={BAR}
                    rx={3}
                    className={`chart-tone--${segment.tone}`}
                  />
                );
              })}
              <text x={barX + barWidth + totalWidth - 6} y={y + ROW / 2} className="chart-value-text" textAnchor="end" dominantBaseline="middle">
                {formatInteger(item.count)}
              </text>
              {hasMetric && (
                <text x={width - 4} y={y + ROW / 2} className="chart-metric-text" textAnchor="end" dominantBaseline="middle">{item.metric ?? "—"}</text>
              )}
            </g>
          );
        })}
      </svg>
      {tip}
      <ChartLegend items={legend}/>
    </div>
  );
}
